import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, IconButton, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Slide1Image from '../images/Slide1.png';
import Slide2Image from '../images/Slide2.png';
import Slide3Image from '../images/Slide3.png';
import Slide4Image from '../images/Slide4.png';
import Slide5Image from '../images/Slide5.png';
import Slide6Image from '../images/Slide6.png';
import Slide7Image from '../images/Slide7.png';

const CarouselContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  background: theme.palette.background.default,
  position: 'relative'
}));

const SlideContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 700,
  borderRadius: (theme.shape.borderRadius as number) * 3,
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
  background: theme.palette.background.paper
}));

const SlideImage = styled(Box)<{ active: boolean }>(({ active }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  opacity: active ? 1 : 0,
  transform: active ? 'scale(1)' : 'scale(1.05)',
  transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(6, 78, 59, 0.3) 0%, rgba(251, 191, 36, 0.2) 100%)',
    zIndex: 1
  }
}));

const SlideContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(4),
  background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
  color: 'white',
  zIndex: 2
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 3,
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  color: theme.palette.primary.main,
  width: 56,
  height: 56,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 1)',
    transform: 'translateY(-50%) scale(1.1)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
  }
}));

const DotsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(3)
}));

const Dot = styled(Box)<{ active: boolean }>(({ theme, active }) => ({
  width: active ? 32 : 12,
  height: 12,
  borderRadius: 6,
  background: active ? theme.palette.primary.main : theme.palette.grey[300],
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    background: active ? theme.palette.primary.dark : theme.palette.grey[400]
  }
}));

const PlayPauseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 3,
  background: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(10px)',
  color: 'white',
  width: 48,
  height: 48,
  '&:hover': {
    background: 'rgba(0, 0, 0, 0.7)'
  }
}));

interface CarouselSlide {
  id: number;
  image: string;
  title: string;
  description: string;
}

const ImageCarousel: React.FC = () => {
  // RiteReview app screenshots
  const slides: CarouselSlide[] = [
    {
      id: 1,
      image: Slide1Image,
      title: 'Lead generation problem?',
      description: 'We got you covered'
    },
    {
      id: 2,
      image: Slide2Image,
      title: 'AI-Powered Review Generation',
      description: 'Transform customer feedback into authentic reviews with our advanced AI technology'
    },
    {
      id: 3,
      image: Slide3Image,
      title: 'Targeted Customer Reach',
      description: 'Upload your customer list and send surveys via email or SMS with ease'
    },
    {
      id: 4,
      image: Slide4Image,
      title: 'Multi-Platform Integration',
      description: 'Direct reviews to Google, Facebook, Instagram and other platforms where they matter most'
    },
    {
      id: 5,
      image: Slide5Image,
      title: 'AI-Powered Reviews',
      description: 'Customers answer questions, AI crafts enthusiastic summaries they can approve and post.'
    },
    {
      id: 6,
      image: Slide6Image,
      title: 'Instant Rewards & UPI Payouts',
      description: 'You reward customers instantly with UPI payments for valuable feedback and quality reviews. Set your own reward amounts when creating surveys.'
    },
    {
      id: 7,
      image: Slide7Image,
      title: 'Admin Panel & Analytics',
      description: 'Comprehensive reports and real-time performance tracking for all your review campaigns'
    }
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isPlaying, slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <CarouselContainer>
      <Container maxWidth="lg">
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              mb: 3,
              color: '#064E3B'
            }}
          >
            See RiteReview in Action
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'text.secondary', 
              maxWidth: 600, 
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            Explore our powerful features through this interactive showcase of the RiteReview platform
          </Typography>
        </Box>

        <Box position="relative">
          <SlideContainer>
            {slides.map((slide, index) => (
              <SlideImage
                key={slide.id}
                active={index === currentSlide}
                sx={{
                  backgroundImage: `url(${slide.image})`
                }}
              >
                <SlideContent>
                  <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    {slide.title}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    {slide.description}
                  </Typography>
                </SlideContent>
              </SlideImage>
            ))}

            <NavigationButton
              onClick={prevSlide}
              sx={{ left: 16 }}
            >
              <ArrowBackIosIcon />
            </NavigationButton>

            <NavigationButton
              onClick={nextSlide}
              sx={{ right: 16 }}
            >
              <ArrowForwardIosIcon />
            </NavigationButton>

            <PlayPauseButton onClick={togglePlayPause}>
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </PlayPauseButton>
          </SlideContainer>

          <DotsContainer>
            {slides.map((_, index) => (
              <Dot
                key={index}
                active={index === currentSlide}
                onClick={() => goToSlide(index)}
              />
            ))}
          </DotsContainer>
        </Box>
      </Container>
    </CarouselContainer>
  );
};

export default ImageCarousel;